import { Box, Button, Flex, Heading, Image, Tag, Text } from "@chakra-ui/react"
import { BlockProductFeedEntityHydrated, EntityId } from "@jackfruit/common"
import React, { useState } from "react"
import { useTranslation } from "react-i18next"
import { useDispatch } from "react-redux"
import { useLocation } from "react-use"
import { useApplicationState } from "~/hooks/useApplicationState"
import { usePrintService } from "~/hooks/usePrintService"
import { useProduct } from "~/hooks/useProduct"
import { useProductImages } from "~/hooks/useProductImages"
import { useProductPrice } from "~/hooks/useProductPrice"
import { actions } from "~/redux/process"
import { useSetPageMetaForProduct } from "./hooks/useSetPageMetaForProduct"
import { createImageFromProduct } from "./utils/imageGeneration"

function runFunction(func: string) {
  // eslint-disable-next-line no-eval
  eval(func)
}

interface Props {
  pageId: EntityId
  productId: EntityId
  config: BlockProductFeedEntityHydrated
}

const ProductFeedContent: React.FC<Props> = ({ pageId, productId, config }) => {
  useSetPageMetaForProduct(productId, pageId)

  const [isLoading, setIsLoading] = useState(false)
  const dispatch = useDispatch()
  const { t } = useTranslation()
  const { html } = config
  const { displayName } = useApplicationState()
  const { href: currentUrl } = useLocation()
  const { product } = useProduct(productId)

  const {
    id: internalProductId,
    longDescription,
    shortDescription,
    printServiceId,
    categoryName,
    size,
    finish,
    metaData,
    prices,
    category,
  } = product
  const { gtin, merchantAvailability } = metaData
  const { printService } = usePrintService(printServiceId)
  const { fulfillmentType } = printService
  const { productImages } = useProductImages(internalProductId)
  const { productPrice } = useProductPrice(prices[0])
  const { currency, total, totalHuman } = productPrice

  const editorFirstCategories = [
    "book",
    "photo-book",
    "calendar",
    "lenticular-card",
  ]

  const buyNowButtonProps: Record<string, string | number | Function> = {
    onClick: async () => {
      setIsLoading(true)
      try {
        const file = await createImageFromProduct(product)
        dispatch(
          actions.createUploadFromBuyNowButton({
            pageId,
            file,
            productId,
            fulfillment: fulfillmentType,
          })
        )
      } catch (error) {
        console.error("Error uploading image:", error)
      } finally {
        setTimeout(() => {
          setIsLoading(false)
        }, 1000)
      }
    },
  }

  let customizeButtonProps: Record<string, string | number | Function> = {
    "data-product-ids": productId,
    "data-product-category-ids": category,
    "data-default-fulfillment": fulfillmentType,
    onClick: () => runFunction("api.uploadImages(event)"),
  }

  if (editorFirstCategories.includes(categoryName)) {
    customizeButtonProps = {
      "data-product-id": productId,
      "data-default-fulfillment": fulfillmentType,
      onClick: () => runFunction("api.addProductToCart(event)"),
    }
  }

  const desktopImage = productImages.find(
    image => image?.metaData?.web?.desktop?.merchant
  )

  const mobileImage = productImages.find(
    image => image?.metaData?.web?.mobile?.merchant
  )

  const jsonLdContent = {
    "@context": "https://schema.org/",
    "@type": "Product",
    name: shortDescription,
    image: desktopImage?.url,
    description: longDescription,
    sku: productId,
    gtin14: gtin,
    brand: {
      "@type": "Brand",
      name: displayName,
    },
    offers: {
      "@type": "Offer",
      url: currentUrl,
      priceCurrency: currency,
      price: total,
      itemCondition: "https://schema.org/NewCondition",
      availability: "https://schema.org/InStock",
    },
  }

  return (
    <>
      <script type="application/ld+json">
        {JSON.stringify(jsonLdContent, null, 2)}
      </script>
      <Flex
        gap={{ base: 6, md: 10 }}
        py="2rem"
        flexDirection={{ base: "column", md: "row" }}
      >
        <Image
          src={desktopImage?.url}
          alt={shortDescription}
          title={shortDescription}
          htmlWidth="400px"
          htmlHeight="400px"
          boxSize="400px"
          width="400px"
          borderRadius="xl"
          objectFit="cover"
          display={{ base: "none", md: "block" }}
        />
        <Image
          src={mobileImage?.url}
          alt={shortDescription}
          title={shortDescription}
          htmlWidth="400px"
          htmlHeight="200px"
          boxSize="200px"
          width={{ base: "full", md: "400px" }}
          borderRadius="xl"
          objectFit="cover"
          display={{ base: "block", md: "none" }}
        />

        <Flex flexDirection="column" gap={4} w="full">
          <Heading as="h1" size="lg" order={{ base: 1 }}>
            {shortDescription}
          </Heading>

          <Flex gap={3} order={{ base: 2 }}>
            <Tag textTransform="capitalize">{categoryName}</Tag>
            {Boolean(merchantAvailability) && (
              <Tag textTransform="capitalize">
                {t("components.blocks.product-feed.ProductFeedContent.inStock")}
              </Tag>
            )}
            {!Boolean(merchantAvailability) && (
              <Tag textTransform="capitalize" colorScheme="red">
                {t(
                  "components.blocks.product-feed.ProductFeedContent.unavailable"
                )}
              </Tag>
            )}
            <Tag>{totalHuman}</Tag>
          </Flex>

          {Boolean(html) && (
            <Box
              order={{ base: 3 }}
              dangerouslySetInnerHTML={{ __html: html }}
            />
          )}

          <Heading as="h2" size="md" order={{ base: 10, md: 10 }}>
            {t("components.blocks.product-feed.ProductFeedContent.features")}
          </Heading>

          <Flex flexDirection="column" gap={2} order={{ base: 11, md: 11 }}>
            <Flex>
              <Text fontWeight="bold">
                {t("components.blocks.product-feed.ProductFeedContent.sizes")} :
              </Text>
              <Text ml={1}>{size}</Text>
            </Flex>
            <Flex>
              <Text fontWeight="bold">
                {t("components.blocks.product-feed.ProductFeedContent.finish")}{" "}
                :
              </Text>
              <Text ml={1}>{finish}</Text>
            </Flex>
          </Flex>

          <Flex
            py={2}
            gap={3}
            flexDirection={{ base: "column", md: "row" }}
            order={{ base: 9, md: 12 }}
          >
            <Button
              size="lg"
              w={{ base: "full", md: "auto" }}
              colorScheme="primary"
              isDisabled={!merchantAvailability}
              isLoading={isLoading}
              {...buyNowButtonProps}
            >
              {t(
                "components.blocks.product-feed.ProductFeedContent.getStarted"
              )}
            </Button>
            <Button
              size="lg"
              w={{ base: "full", md: "auto" }}
              colorScheme="primary"
              variant="outline"
              _hover={{ bg: "transparent" }}
              isDisabled={!merchantAvailability}
              {...customizeButtonProps}
            >
              {t("components.blocks.product-feed.ProductFeedContent.customize")}
            </Button>
          </Flex>

          <Heading as="h2" size="md" order={{ base: 13 }}>
            {t("components.blocks.product-feed.ProductFeedContent.description")}
          </Heading>
          <Flex minHeight="120px" order={{ base: 14 }}>
            <Box dangerouslySetInnerHTML={{ __html: longDescription }} />
          </Flex>
        </Flex>
      </Flex>
    </>
  )
}

export default ProductFeedContent
